// src/utils/expenseDataAdapters.js
// View-layer normalization for the Expense Overview page and report selectors.
// Reads from existing hook data shapes — does NOT modify storage.
// Each normalizer returns a common shape for display-only purposes.

/**
 * Normalize a project expense row for unified overview display.
 * @param {Object} expense — camelCase object from useSupabaseExpenses / activeExpenses
 * @returns {Object} normalized row
 */
export function normalizeProjectExpense(expense) {
  return {
    id: `proj-${expense.id}`,
    sourceType: 'project_expense',
    sourceRecordId: expense.id,
    sourcePage: 'project-expenses',
    date: expense.date || null,
    amount: expense.amountInInr ?? expense.amount ?? 0,
    amountOriginal: expense.amountOriginal ?? expense.amount ?? 0,
    currencyOriginal: expense.currencyOriginal || 'INR',
    fxRateToInr: expense.fxRateToInr ?? 1,
    category: expense.category || '',
    description: expense.description || '',
    projectId: expense.projectId || null,
    employeeId: null,
    vendor: null,
    status: expense.status || 'planned',
    officeLocation: normalizeOffice(expense.officeLocation),
    clientBillable: expense.clientBillable || false,
    billingStatus: expense.billingStatus || null,
    importSource: expense.importSource || null,
  };
}

/**
 * Normalize an office overhead row for unified overview display.
 * @param {Object} overhead — camelCase object from useSupabaseOfficeOverheads / activeOfficeOverheads
 * @returns {Object} normalized row
 */
export function normalizeOfficeExpense(overhead) {
  return {
    id: `office-${overhead.id}`,
    sourceType: 'office_expense',
    sourceRecordId: overhead.id,
    sourcePage: 'office-expenses',
    date: overhead.expenseDate || (overhead.year ? `${overhead.year}-01-01` : null),
    amount: overhead.amountInInr ?? overhead.amount ?? 0,
    amountOriginal: overhead.amountOriginal ?? overhead.amount ?? 0,
    currencyOriginal: overhead.currencyOriginal || 'INR',
    fxRateToInr: overhead.fxRateToInr ?? 1,
    category: overhead.category || '',
    description: overhead.subcategory || '',
    projectId: null,
    employeeId: null,
    vendor: null,
    status: null,
    officeLocation: normalizeOffice(overhead.officeLocation),
    clientBillable: false,
    billingStatus: null,
    importSource: overhead.importSource || null,
  };
}

/**
 * Normalize a reimbursement row for unified overview display.
 * @param {Object} reimbursement — camelCase object from useSupabaseReimbursements
 * @returns {Object} normalized row
 */
export function normalizeReimbursement(reimbursement) {
  return {
    id: `reimb-${reimbursement.id}`,
    sourceType: 'reimbursement',
    sourceRecordId: reimbursement.id,
    sourcePage: 'reimbursements',
    date: reimbursement.expenseDate || null,
    amount: reimbursement.amountInInr ?? reimbursement.amount ?? 0,
    amountOriginal: reimbursement.amount ?? 0,
    currencyOriginal: reimbursement.currency || 'INR',
    fxRateToInr: reimbursement.fxRateToInr ?? 1,
    category: reimbursement.category || '',
    description: reimbursement.description || '',
    projectId: reimbursement.projectId || null,
    employeeId: reimbursement.userId || null,
    vendor: reimbursement.vendor || null,
    status: reimbursement.status || 'pending',
    officeLocation: normalizeOffice(reimbursement.officeLocation),
    clientBillable: reimbursement.clientBillable ?? false,
    billingStatus: reimbursement.billingStatus || null,
    importSource: reimbursement.importSource || null,
    _isReimbursement: true,
  };
}

/**
 * Normalize a payment-inbox expense event for unified overview display.
 * @param {Object} evt — expense payment event from invoicesHook.expensePayments
 * @returns {Object} normalized row
 */
export function normalizePaymentInboxExpense(evt) {
  return {
    id: `pmt-${evt.id}`,
    sourceType: 'payment_inbox_expense',
    sourceRecordId: evt.id,
    sourcePage: 'project-expenses',
    date: evt.txnDate || null,
    amount: evt.amount ?? 0,
    amountOriginal: evt.amount ?? 0,
    currencyOriginal: 'INR',
    fxRateToInr: 1,
    category: evt.expenseCategory || '',
    description: evt.narration || '',
    projectId: evt.linkedProjectId || null,
    employeeId: null,
    vendor: null,
    status: evt.linkedProjectId ? 'linked' : 'unlinked',
    officeLocation: normalizeOffice(evt.expenseOffice),
    clientBillable: false,
    billingStatus: null,
    importSource: 'bank-import',
  };
}

// ── Helpers ────────────────────────────────────────────────────────────

function normalizeOffice(loc) {
  if (!loc) return 'NL';
  if (loc === 'Rotterdam') return 'NL';
  if (loc === 'Mumbai') return 'IN';
  return loc;
}

// ── Aggregate builders (used by ExpenseOverviewPage) ───────────────────

/**
 * Build a unified expense list from all sources.
 * Returns an array of normalized rows sorted by date descending.
 */
export function buildUnifiedExpenseList({
  projectExpenses = [],
  officeOverheads = [],
  reimbursements = [],
  paymentInboxExpenses = [],
}) {
  // Reimbursements linked to an imported expense are kept visible but flagged
  // so they can be excluded from totals (the expense record already covers the cost).
  const all = [
    ...projectExpenses.map(normalizeProjectExpense),
    ...officeOverheads.map(normalizeOfficeExpense),
    ...reimbursements.map(r => {
      const norm = normalizeReimbursement(r);
      if (r.linkedExpenseId) norm._isLinkedReceipt = true;
      return norm;
    }),
    ...paymentInboxExpenses.map(normalizePaymentInboxExpense),
  ];

  // Sort by date descending, nulls last
  all.sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return b.date.localeCompare(a.date);
  });

  return all;
}

/**
 * Apply filters to a unified expense list.
 * @param {Array} rows - normalized rows from buildUnifiedExpenseList
 * @param {Object} filters
 * @returns {Array} filtered rows
 */
export function filterUnifiedExpenses(rows, filters = {}) {
  let result = rows;

  if (filters.dateFrom) {
    result = result.filter(r => r.date && r.date >= filters.dateFrom);
  }
  if (filters.dateTo) {
    result = result.filter(r => r.date && r.date <= filters.dateTo);
  }
  if (filters.sourceTypes && filters.sourceTypes.length > 0) {
    result = result.filter(r => filters.sourceTypes.includes(r.sourceType));
  }
  if (filters.status) {
    result = result.filter(r => r.status === filters.status);
  }
  if (filters.projectId) {
    result = result.filter(r => String(r.projectId) === String(filters.projectId));
  }
  if (filters.employeeId) {
    result = result.filter(r => String(r.employeeId) === String(filters.employeeId));
  }
  if (filters.officeLocation) {
    result = result.filter(r => r.officeLocation === filters.officeLocation);
  }
  if (filters.category) {
    result = result.filter(r => r.category === filters.category);
  }
  if (filters.vendor) {
    const v = filters.vendor.toLowerCase();
    result = result.filter(r => r.vendor && r.vendor.toLowerCase().includes(v));
  }

  return result;
}

/**
 * Compute totals from a filtered unified expense list.
 * @param {Array} rows - normalized (and optionally filtered) rows
 * @param {Function} convertMoney - from useSettings
 * @param {string} displayCurrency
 * @returns {Object} { total, byType, byProject, byCategory, byOffice }
 */
export function computeExpenseTotals(rows, convertMoney, displayCurrency) {
  const byType = {};
  const byProject = {};
  const byCategory = {};
  const byOffice = {};
  let total = 0;

  for (const r of rows) {
    // Linked receipts are shown in tables but excluded from totals
    // (the imported expense already covers this cost).
    if (r._isLinkedReceipt) continue;

    const amt = convertMoney
      ? convertMoney(r.amount, 'INR', displayCurrency, r.date)
      : r.amount;

    total += amt;

    // By type
    byType[r.sourceType] = (byType[r.sourceType] || 0) + amt;

    // By project
    if (r.projectId) {
      byProject[r.projectId] = (byProject[r.projectId] || 0) + amt;
    }

    // By category
    if (r.category) {
      byCategory[r.category] = (byCategory[r.category] || 0) + amt;
    }

    // By office
    if (r.officeLocation) {
      byOffice[r.officeLocation] = (byOffice[r.officeLocation] || 0) + amt;
    }
  }

  return { total, byType, byProject, byCategory, byOffice };
}

// ── Source type labels (for UI display) ────────────────────────────────

export const SOURCE_TYPE_LABELS = {
  project_expense: 'Project Expense',
  office_expense: 'Office Expense',
  reimbursement: 'Reimbursement',
  payment_inbox_expense: 'Payment Inbox',
};

export const SOURCE_TYPE_OPTIONS = Object.entries(SOURCE_TYPE_LABELS).map(
  ([value, label]) => ({ value, label })
);
